import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Grid, 
  Typography, 
  Button, 
  TextField, 
  Paper, 
  Chip, 
  Divider, 
  Fade,
  Avatar
} from "@mui/material";
import { 
  CheckCircle2, 
  Clock, 
  Plus, 
  FileText, 
  MessageSquare, 
  Calendar as CalendarIcon,
  ChevronRight,
  ClipboardList 
} from "lucide-react";
import { toast } from "react-toastify";
import AgregarTareaModal from "../Comunicacion/Tareas/TareasModal";
import SubirArchivoComunicacion from "../Archivos/SubirArchivoComunicacion";
import ListadoArchivosComunicacion from "../Archivos/ListadoArchivosTareaComunicacion";
import ListadoArchivosPedidoComunicacion from "../Archivos/ListadoArchivosPedidoComunicacion";

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  pearl: "#EEF3F4",
  crema: "#F3F2B3",
  aqua: "#00C0CA",
  lime: "#8FC440",
  teal: "#008B9C",
  dark: "#22404D",
  red: "#F65F4E",
  yellow: "#E3E24F"
};

const DetallePedido = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);
  const [comentario, setComentario] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [aux, setAux] = useState(0);
  const [actualizarPedido, setActualizarPedido] = useState(0);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchPedido = async () => {
      try {
        const response = await fetch(`${apiUrl}/Pedido/ObtenerPedidoPorID?id=${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            apiKey: localStorage.getItem("apiKey"),
          },
        });
        if (!response.ok) throw new Error("Error fetching pedido");
        const data = await response.json();
        setPedido(data);
      } catch (error) {
        console.error(error);
        navigate("/");
      }
    };
    fetchPedido();
  }, [id, apiUrl, actualizarPedido, navigate]);

  useEffect(() => {
    setComentario(tareaSeleccionada?.comentario || "");
  }, [tareaSeleccionada]);

  const terminarTarea = async () => {
    if (!tareaSeleccionada) return;
    try {
      const estado = {
        idTarea: tareaSeleccionada.id,
        idPedido: id,
        comentario: comentario.trim() || null,
      };

      const response = await fetch(`${apiUrl}/Pedido/Estado`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apiKey: localStorage.getItem("apiKey"),
        },
        body: JSON.stringify(estado),
      });

      if (response.ok) {
        toast.success("Tarea completada exitosamente");
        setActualizarPedido(prev => prev + 1);
        setTareaSeleccionada(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      
      {/* Cabecera del Pedido */}
      <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 900, color: PALETTE.dark }}>
              {pedido?.nombre}
            </Typography>
            <Chip 
              label={pedido?.estado} 
              color={pedido?.estado === "Terminado" ? "success" : "warning"}
              sx={{ fontWeight: 700, borderRadius: '8px' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: PALETTE.azur }}>
            <CalendarIcon size={18} />
            <Typography variant="body1" fontWeight={500}>
              Fecha Límite: {new Date(pedido?.fechaLimite).toLocaleDateString("es-ES")}
            </Typography>
          </Box>
        </Box>

        {localStorage.getItem("rol") === "coordinador" && pedido?.estado !== "Terminado" && (
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={() => setShowModal(true)}
            sx={{ 
              bgcolor: PALETTE.dark, borderRadius: '12px', px: 3, py: 1.5,
              '&:hover': { bgcolor: PALETTE.teal }
            }}
          >
            Agregar Tarea
          </Button>
        )}
      </Box>

      <Grid container spacing={4}>
        {/* Columna Izquierda: Lista de Tareas */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, color: PALETTE.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClipboardList size={20} /> Tareas del Pedido
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pedido?.tareas.map((tarea) => (
              <Paper
                key={tarea.id}
                elevation={0}
                onClick={() => setTareaSeleccionada(tarea)}
                sx={{
                  p: 2, borderRadius: '20px', cursor: 'pointer',
                  border: `2px solid ${tareaSeleccionada?.id === tarea.id ? PALETTE.aqua : '#f0f0f0'}`,
                  backgroundColor: tareaSeleccionada?.id === tarea.id ? `${PALETTE.ice}30` : 'white',
                  transition: '0.3s',
                  '&:hover': { transform: 'translateX(8px)', borderColor: PALETTE.sky }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: tarea.estado === "Terminado" ? PALETTE.lime : PALETTE.yellow, width: 32, height: 32 }}>
                      {tarea.estado === "Terminado" ? <CheckCircle2 size={18} /> : <Clock size={18} />}
                    </Avatar>
                    <Typography fontWeight={700} color={PALETTE.dark}>
                      {tarea.tipoTarea === "Disenio" ? "Diseño" : tarea.tipoTarea}
                    </Typography>
                  </Box>
                  <ChevronRight size={18} color={PALETTE.sky} />
                </Box>
              </Paper>
            ))}
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <ListadoArchivosPedidoComunicacion pedidoId={id} actualizar={0} />
          </Box>
        </Grid>

        {/* Columna Derecha: Detalle de Tarea Seleccionada */}
        <Grid item xs={12} md={8}>
          {tareaSeleccionada ? (
            <Fade in={true}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: '32px', border: `1px solid ${PALETTE.sky}`, bgcolor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark }}>
                    Detalle de Tarea
                  </Typography>
                  <Chip 
                    label={tareaSeleccionada.estado}
                    sx={{ 
                      bgcolor: tareaSeleccionada.estado === "Terminado" ? PALETTE.lime : PALETTE.crema,
                      fontWeight: 800, color: PALETTE.dark
                    }}
                  />
                </Box>

                <Grid container spacing={2}>
                  {Object.entries(tareaSeleccionada).map(([key, value]) => {
                    if (["id", "comentario", "estado", "nombre"].includes(key)) return null;
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="caption" sx={{ color: PALETTE.teal, fontWeight: 700, textTransform: 'uppercase' }}>
                          {key}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: PALETTE.dark }}>
                          {typeof value === "boolean" ? (value ? "Sí" : "No") : 
                           (typeof value === "string" && value.includes("T") ? new Date(value).toLocaleDateString("es-ES") : value)}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>

                {tareaSeleccionada.comentario && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: '15px', borderLeft: `5px solid ${PALETTE.sky}` }}>
                    <Typography variant="subtitle2" fontWeight={800} color={PALETTE.teal}>Comentario del equipo:</Typography>
                    <Typography variant="body2">{tareaSeleccionada.comentario}</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 4 }} />

                {/* Área de Gestión para Comunicación */}
                {localStorage.getItem("rol") === "comunicacion" && tareaSeleccionada.estado !== "Terminado" ? (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MessageSquare size={20} /> Responder y Finalizar
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Escribe aquí tus observaciones o detalles finales..."
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: '18px', bgcolor: 'white' } }}
                    />
                    <SubirArchivoComunicacion 
                      pedidoId={id} 
                      tareaId={tareaSeleccionada.id} 
                      actualizar={() => setAux(aux + 1)} 
                    />
                    <Box sx={{ mt: 2 }}>
                      <ListadoArchivosComunicacion pedidoId={id} tareaId={tareaSeleccionada.id} actualizar={aux} />
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={terminarTarea}
                      sx={{ 
                        mt: 4, py: 2, borderRadius: '15px', fontWeight: 800,
                        bgcolor: PALETTE.aqua, '&:hover': { bgcolor: PALETTE.teal }
                      }}
                    >
                      Marcar Tarea como Completada
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>Archivos Adjuntos</Typography>
                    <ListadoArchivosComunicacion pedidoId={id} tareaId={tareaSeleccionada.id} actualizar={aux} />
                  </Box>
                )}
              </Paper>
            </Fade>
          ) : (
            <Box sx={{ 
              height: '400px', display: 'flex', flexDirection: 'column', 
              alignItems: 'center', justifyContent: 'center', 
              border: `2px dashed ${PALETTE.sky}`, borderRadius: '32px', color: PALETTE.sky
            }}>
              <FileText size={64} strokeWidth={1} />
              <Typography variant="h6" sx={{ mt: 2 }}>Selecciona una tarea para gestionar</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <AgregarTareaModal
        pedidoId={id}
        show={showModal}
        handleClose={() => { setShowModal(false); setActualizarPedido(prev => prev + 1); }}
      />
    </Box>
  );
};

export default DetallePedido;
